/* ============================================================
   WEALTHSIMPLE SHOP — SHOPIFY THEME JS
   ------------------------------------------------------------
   All client-side behaviors. Notable adaptations from prototype:
     - SPA hash routing stripped; pages are real Shopify URLs
     - Cart stub replaced with /cart/add.js + /cart.js calls
     - JS-based i18n stripped; Liquid {{ 'key' | t }} handles it
     - Variant selection reads inline product JSON written by
       sections/main-product.liquid into #pdpProductData.
   ============================================================ */
(function () {
  'use strict';

  /* ===== Money formatter (Shopify cart API returns cents) ===== */
  function formatMoney(cents, currency) {
    if (cents == null) return '';
    if (cents === 0) return 'MSRP';
    var dollars = (cents / 100);
    // Match the WS preference: no trailing zeros, comma thousands, $ prefix.
    var s = dollars.toLocaleString('en-CA', {
      minimumFractionDigits: dollars % 1 === 0 ? 0 : 2,
      maximumFractionDigits: 2
    });
    return '$' + s;
  }

  /* ===== Locale helpers ===== */
  function getLang() {
    // Path-based detection — works even when FR is unpublished
    if (window.location.pathname.indexOf('/fr-ca') === 0) return 'fr';
    return 'en';
  }

  function pick(value) {
    // If value is { en: '...', fr: '...' }, return the right locale variant.
    // Otherwise return the value unchanged.
    if (value && typeof value === 'object' && !Array.isArray(value) && value.en !== undefined) {
      return value[getLang()] || value.en;
    }
    return value;
  }

  /* ===== Localized UI strings (used by cart, PDP, quick-add) ===== */
  var STRINGS = {
    adding:      { en: 'Adding…',                 fr: 'Ajout en cours…' },
    added:       { en: 'Added ✓',                 fr: 'Ajouté ✓' },
    addedCheck:  { en: '✓ Added',                 fr: '✓ Ajouté' },
    addToCart:   { en: 'Add to cart',             fr: 'Ajouter au panier' },
    soldOut:     { en: 'Sold out',                fr: 'Épuisé' },
    error:       { en: 'Error — try again',       fr: 'Erreur — réessayez' },
    unavailable: { en: 'Unavailable',             fr: 'Indisponible' },
    nextImage:   { en: 'Next image',              fr: 'Image suivante' },
    backToFirst: { en: 'Back to first image',     fr: 'Retour à la première image' },
    cartLabel:   { en: 'Cart',                    fr: 'Panier' },
    cartItem:    { en: 'item',                    fr: 'article' },
    closeMenu:   { en: 'Close menu',              fr: 'Fermer le menu' }
  };

  /* ============================================================
     DRAWER + HAMBURGER
     ============================================================ */
  function getDrawer() { return document.getElementById('drawer'); }
  function getOverlay() { return document.getElementById('drawerOverlay'); }
  function getHamburger() { return document.getElementById('hamburgerBtn'); }

  function openDrawer() {
    var drawer = getDrawer(), overlay = getOverlay(), btn = getHamburger();
    if (!drawer || !overlay || !btn) return;
    drawer.classList.add('open');
    overlay.style.display = 'block';
    requestAnimationFrame(function () { overlay.classList.add('visible'); });
    btn.classList.add('open');
    btn.setAttribute('aria-expanded', 'true');
    btn.setAttribute('aria-label', pick(STRINGS.closeMenu));
    document.body.style.overflow = 'hidden';
    // Restore last sub-view (so Shop / Support / About persists across reopens)
    var savedView = 'main';
    try { savedView = sessionStorage.getItem('_megaView') || 'main'; } catch (e) {}
    setMegaView(savedView);
  }

  function closeDrawer() {
    var drawer = getDrawer(), overlay = getOverlay(), btn = getHamburger();
    if (!drawer || !overlay || !btn) return;
    drawer.classList.remove('open');
    overlay.classList.remove('visible');
    btn.classList.remove('open');
    btn.setAttribute('aria-expanded', 'false');
    btn.setAttribute('aria-label', 'Menu');
    document.body.style.overflow = '';
    setTimeout(function () { overlay.style.display = 'none'; }, 500);
    // Note: no longer reset mega view on close — sessionStorage retains last sub-view
  }

  function toggleDrawer() {
    var drawer = getDrawer();
    if (!drawer) return;
    if (drawer.classList.contains('open')) closeDrawer(); else openDrawer();
  }

  /* Mobile mega-menu view switching (main / shop / support / about) */
  function setMegaView(view) {
    var grid = document.querySelector('.mega-menu-grid');
    if (grid) grid.setAttribute('data-mobile-view', view);
    try { sessionStorage.setItem('_megaView', view); } catch (e) {}
  }

  // expose for inline onclick fallbacks (drawer markup uses listeners only)
  window.openDrawer = openDrawer;
  window.closeDrawer = closeDrawer;
  window.toggleDrawer = toggleDrawer;
  window.setMegaView = setMegaView;

  /* ============================================================
     INTRO REVEAL
     The prototype hides .reveal-line and .ia elements with opacity:0
     until .intro-headline.revealed / .ia.played are toggled. The SPA
     used to handle this; we now do it on DOMContentLoaded with a
     short stagger so the animation still feels intentional.
     ============================================================ */
  function initIntroReveal() {
    var headline = document.querySelector('.intro-headline');
    if (headline) {
      // Stagger the .reveal-line spans by giving them per-child transition-delay
      var lines = headline.querySelectorAll('.reveal-line');
      lines.forEach(function (el, i) {
        el.style.transitionDelay = (i * 120) + 'ms';
      });
      requestAnimationFrame(function () {
        requestAnimationFrame(function () { headline.classList.add('revealed'); });
      });
    }
    document.querySelectorAll('.ia').forEach(function (el, i) {
      setTimeout(function () { el.classList.add('played'); }, 50 + i * 80);
    });
  }

  /* ============================================================
     HERO CROSS-FADE + PARALLAX
     ============================================================ */
  function initHeroCrossfade() {
    var imgs = document.querySelectorAll('.intro-right .hero-img');
    if (imgs.length < 2) return;
    var idx = 0;
    setInterval(function () {
      imgs[idx].classList.remove('is-active');
      idx = (idx + 1) % imgs.length;
      imgs[idx].classList.add('is-active');
    }, 4000);
  }

  function initIntroParallax() {
    var imgs = document.querySelectorAll('.intro-right img');
    if (!imgs.length) return;
    var ticking = false;
    function update() {
      var y = window.scrollY || window.pageYOffset || 0;
      imgs.forEach(function (img) {
        var scale = parseFloat(img.dataset.scale || '1') || 1;
        img.style.transform = 'translateY(' + (y * 0.22) + 'px) scale(' + scale + ')';
      });
      ticking = false;
    }
    window.addEventListener('scroll', function () {
      if (!ticking) {
        window.requestAnimationFrame(update);
        ticking = true;
      }
    }, { passive: true });
    update();
  }

  /* ============================================================
     INTRO CTA SCROLL-TO
     ============================================================ */
  function initScrollTo() {
    document.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-scroll-to]');
      if (!btn) return;
      var target = document.querySelector(btn.dataset.scrollTo);
      if (target) {
        e.preventDefault();
        var y = target.getBoundingClientRect().top + window.scrollY - 80;
        window.scrollTo({ top: y, behavior: 'smooth' });
      }
    });
  }

  /* ============================================================
     FAQ ACCORDION
     ============================================================ */
  function initFaq() {
    document.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-faq-toggle]');
      if (!btn) return;
      var item = btn.closest('.faq-item');
      if (!item) return;
      var isOpen = item.classList.contains('open');
      // Close siblings (only one open at a time)
      var siblings = item.parentElement.querySelectorAll('.faq-item.open');
      siblings.forEach(function (s) { s.classList.remove('open'); });
      if (!isOpen) item.classList.add('open');
    });
  }

  /* ============================================================
     PDP — variant selection, qty, add-to-cart
     ============================================================ */
  var _pdpProduct = null;
  function getPdpProduct() {
    if (_pdpProduct !== null) return _pdpProduct;
    var dataEl = document.getElementById('pdpProductData');
    if (!dataEl) { _pdpProduct = false; return false; }
    try {
      _pdpProduct = JSON.parse(dataEl.textContent);
    } catch (err) {
      console.error('Could not parse PDP product data', err);
      _pdpProduct = false;
    }
    return _pdpProduct;
  }

  function getCurrentSelectedOptions() {
    // Read selected option values from .ctrl-color.active and .ctrl-size.active
    var product = getPdpProduct();
    if (!product) return null;
    var opts = product.options.slice(); // length matches options[]
    var arr = new Array(product.options.length).fill(null);

    var activeColor = document.querySelector('.ctrl-color.active');
    if (activeColor) {
      var idx = parseInt(activeColor.dataset.optionIndex, 10);
      arr[idx] = activeColor.dataset.optionValue;
    }
    var activeSize = document.querySelector('.ctrl-size.active');
    if (activeSize) {
      var idx2 = parseInt(activeSize.dataset.optionIndex, 10);
      arr[idx2] = activeSize.dataset.optionValue;
    }
    // Fill any unset positions from the first variant
    if (product.variants.length) {
      var first = product.variants[0];
      var firstOpts = [first.option1, first.option2, first.option3];
      for (var i = 0; i < arr.length; i++) {
        if (arr[i] == null) arr[i] = firstOpts[i];
      }
    }
    return arr;
  }

  function findVariantByOptions(arr) {
    var product = getPdpProduct();
    if (!product || !arr) return null;
    return product.variants.find(function (v) {
      var vOpts = [v.option1, v.option2, v.option3];
      for (var i = 0; i < arr.length; i++) {
        if (arr[i] != null && vOpts[i] !== arr[i]) return false;
      }
      return true;
    }) || null;
  }

  function updatePdpForVariant(variant) {
    if (!variant) return;
    var input = document.getElementById('pdpVariantId');
    if (input) input.value = variant.id;

    // Update price display
    var priceEl = document.getElementById('pdpPrice');
    if (priceEl) {
      if (variant.price === 0) {
        priceEl.textContent = 'MSRP';
      } else if (variant.compare_at_price && variant.compare_at_price > variant.price) {
        priceEl.innerHTML =
          '<span class="price-current">' + formatMoney(variant.price) + '</span>' +
          '<span class="price-old">' + formatMoney(variant.compare_at_price) + '</span>';
      } else {
        priceEl.textContent = formatMoney(variant.price);
      }
    }

    // Update cart button availability
    var cartBtn = document.getElementById('cartBtn');
    if (cartBtn && cartBtn.tagName === 'BUTTON') {
      if (variant.available) {
        cartBtn.disabled = false;
        cartBtn.classList.remove('sold-out');
        cartBtn.textContent = cartBtn.dataset.labelAdd || pick(STRINGS.addToCart);
      } else {
        cartBtn.disabled = true;
        cartBtn.classList.add('sold-out');
        cartBtn.textContent = cartBtn.dataset.labelSoldOut || pick(STRINGS.soldOut);
      }
    }

    // Update URL ?variant= so refresh preserves state
    if (window.history && window.history.replaceState) {
      var url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url.toString());
    }
  }

  function initPdpVariantSelection() {
    var product = getPdpProduct();
    if (!product) return;

    // Cache labels for re-use
    var cartBtn = document.getElementById('cartBtn');
    if (cartBtn && cartBtn.tagName === 'BUTTON' && !cartBtn.dataset.labelAdd) {
      cartBtn.dataset.labelAdd = cartBtn.textContent.trim();
      cartBtn.dataset.labelSoldOut = pick(STRINGS.soldOut);
    }

    // Color buttons
    document.querySelectorAll('.ctrl-color').forEach(function (btn) {
      btn.addEventListener('click', function () {
        document.querySelectorAll('.ctrl-color').forEach(function (b) { b.classList.remove('active'); });
        btn.classList.add('active');
        var nameEl = document.querySelector('[data-color-name]');
        if (nameEl) nameEl.textContent = btn.dataset.optionValue;
        var v = findVariantByOptions(getCurrentSelectedOptions());
        if (v) updatePdpForVariant(v);
      });
    });

    // Size buttons
    document.querySelectorAll('.ctrl-size').forEach(function (btn) {
      btn.addEventListener('click', function () {
        if (btn.classList.contains('unavailable')) return;
        document.querySelectorAll('.ctrl-size').forEach(function (b) { b.classList.remove('active'); });
        btn.classList.add('active');
        var v = findVariantByOptions(getCurrentSelectedOptions());
        if (v) updatePdpForVariant(v);
      });
    });
  }

  function initPdpQty() {
    var num = document.getElementById('qtyNum');
    var hidden = document.getElementById('pdpQuantity');
    var down = document.getElementById('qtyDown');
    var up = document.getElementById('qtyUp');
    if (!num || !hidden) return;
    function set(n) {
      n = Math.max(1, n);
      num.textContent = n;
      hidden.value = n;
    }
    if (down) down.addEventListener('click', function () { set(parseInt(num.textContent, 10) - 1); });
    if (up) up.addEventListener('click', function () { set(parseInt(num.textContent, 10) + 1); });
  }

  function initPdpAddToCart() {
    var form = document.querySelector('[data-pdp-form]');
    if (!form) return;
    form.addEventListener('submit', function (e) {
      e.preventDefault();
      var idInput = document.getElementById('pdpVariantId');
      var qtyInput = document.getElementById('pdpQuantity');
      var btn = document.getElementById('cartBtn');
      if (!idInput || !qtyInput) return;
      if (btn) {
        btn.disabled = true;
        btn.dataset.prevText = btn.textContent;
        btn.textContent = pick(STRINGS.adding);
      }
      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ id: idInput.value, quantity: parseInt(qtyInput.value, 10) || 1 })
      })
        .then(function (r) {
          if (!r.ok) return r.json().then(function (err) { throw err; });
          return r.json();
        })
        .then(function () {
          return refreshCart();
        })
        .then(function () {
          if (btn) {
            btn.textContent = pick(STRINGS.added);
            bumpCartBadge();
            setTimeout(function () {
              btn.textContent = btn.dataset.prevText || pick(STRINGS.addToCart);
              btn.disabled = false;
            }, 1500);
          }
        })
        .catch(function (err) {
          console.error('cart/add failed', err);
          if (btn) {
            btn.textContent = (err && err.description) || pick(STRINGS.error);
            setTimeout(function () {
              btn.textContent = btn.dataset.prevText || pick(STRINGS.addToCart);
              btn.disabled = false;
            }, 2200);
          }
        });
    });
  }

  /* PDP dual-pane scroll handoff (desktop only).
     When the right pane scrolls past its edge, pass the wheel delta to window. */
  function initPdpScrollHandoff() {
    var rightScroll = document.querySelector('.pdp-info-scroll');
    if (!rightScroll) return;
    rightScroll.addEventListener('wheel', function (e) {
      var atTop = rightScroll.scrollTop === 0;
      var atBottom = Math.ceil(rightScroll.scrollTop + rightScroll.clientHeight) >= rightScroll.scrollHeight;
      if ((e.deltaY < 0 && atTop) || (e.deltaY > 0 && atBottom)) {
        // let window handle it; default behavior
      }
    }, { passive: true });
  }

  /* Mobile sticky add-to-cart (PDP only). Mirror main button when offscreen. */
  function initMobileStickyCart() {
    var main = document.getElementById('cartBtn');
    var sticky = document.getElementById('mobileStickyCart');
    var stickyBtn = document.getElementById('mobileStickyCartBtn');
    if (!main || !sticky || !stickyBtn) return;
    if (!('IntersectionObserver' in window)) return;

    // Combined state — sticky shows only when main btn is offscreen
    // AND the footer is not yet in view
    var mainBtnOffscreen = false;
    var footerVisible = false;

    function updateStickyState() {
      if (mainBtnOffscreen && !footerVisible) {
        sticky.classList.add('visible');
        sticky.setAttribute('aria-hidden', 'false');
      } else {
        sticky.classList.remove('visible');
        sticky.setAttribute('aria-hidden', 'true');
      }
    }

    // Watch main Add to Cart button
    var ioMain = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        mainBtnOffscreen = !entry.isIntersecting;
        updateStickyState();
      });
    }, { threshold: 0.1 });
    ioMain.observe(main);

    // Watch footer — when it enters viewport, hide sticky so user can
    // reach Privacy/Terms/copyright links uncovered
    var footer = document.querySelector('.footer-bottom');
    if (footer) {
      var ioFooter = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          footerVisible = entry.isIntersecting;
          updateStickyState();
        });
      }, { threshold: 0 });
      ioFooter.observe(footer);
    }

    stickyBtn.addEventListener('click', function () {
      if (main.click) main.click();
    });
  }

  /* ============================================================
     LIGHTBOX
     ============================================================ */
  function openLightbox(src, alt) {
    var lb = document.getElementById('lightbox');
    var img = document.getElementById('lightboxImg');
    if (!lb || !img) return;
    img.src = src;
    img.alt = alt || '';
    lb.classList.add('open');
    lb.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
  }
  function closeLightbox() {
    var lb = document.getElementById('lightbox');
    if (!lb) return;
    lb.classList.remove('open');
    lb.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
  }
  window.openLightbox = openLightbox;
  window.closeLightbox = closeLightbox;

  /* ============================================================
     SIZE GUIDES — shared between PDP modal and standalone page
     ============================================================ */
  var SIZE_GUIDES = [
    {
      handle: 'crewneck-sweater',
      handleAliases: ['crewneck', 'crewneck-sweater', 'sweater'],
      locales: ['en'],
      name: 'Crewneck sweater',
      meta: 'Vintage fit · Combed French terry',
      sizes: ['XS', 'S', 'M', 'L', 'XL', 'XXL'],
      measures: [
        { label: 'Length', in: [26.00, 27.00, 28.00, 29.00, 30.00, 31.00] },
        { label: 'Chest',  in: [20.25, 21.50, 22.75, 24.00, 25.25, 26.50] },
        { label: 'Sleeve', in: [33.00, 33.75, 34.75, 35.75, 36.50, 37.25] }
      ],
      illustration: 'Crewneck_Spec_Illustration.png',
      illustrationAlt: 'Crewneck measurement illustration',
      fitNote: 'This style has a more tailored fit. If you are in between sizes, or if you want a roomier fit, please size up.'
    },
    {
      handle: 'oversized-tote',
      handleAliases: ['oversized-tote', 'tote', 'tote-bag', 'totebag'],
      locales: ['en'],
      name: 'Oversized tote',
      meta: 'One size · Heavyweight cotton canvas · Made in Canada',
      sizes: ['One Size'],
      measures: [
        { label: 'Width',       in: [21.75] },
        { label: 'Height',      in: [12.00] },
        { label: 'Handle drop', in: [11.50] }
      ],
      illustration: 'Totebag_Spec_Illustration.png',
      illustrationAlt: 'Tote bag measurement illustration',
      fitNote: 'A roomy one-size-fits-all carry — built to comfortably hang from a shoulder.'
    },
    {
      handle: 'soccer-jersey',
      handleAliases: ['soccer-jersey', 'chandail-de-soccer', 'jersey', 'chandail'],
      locales: ['en', 'fr'],
      name: { en: 'Soccer jersey', fr: 'Chandail de soccer' },
      meta: { en: 'Standard fit · 6 sizes', fr: 'Coupe standard · 6 tailles' },
      groupLabel: { en: 'MEN', fr: 'HOMMES' },
      sizes: {
        en: ['XS', 'S', 'M', 'L', 'XL', 'XXL'],
        fr: ['TP',  'P', 'M', 'G', 'TG', '2TG']
      },
      measures: [
        { label: { en: 'Length',                   fr: 'Longueur' },                          in: [26.70, 28.10, 29.10, 30.10, 31.10, 32.10] },
        { label: { en: 'Chest width (Front Only)', fr: 'Largeur poitrine (avant seulement)' }, in: [16.90, 18.10, 20.10, 22.00, 24.00, 25.00] },
        { label: { en: 'Short sleeve length',      fr: 'Manche courte' },                     in: [7.50,  7.90,  8.30,  8.70,  9.10,  9.90]  }
      ],
      illustration: 'Jersey_Spec_Illustration.png',
      illustrationAlt: { en: 'Soccer jersey measurement illustration', fr: 'Illustration des mesures du chandail de soccer' },
      fitNote: {
        en: 'A relaxed, sport-cut fit through the body. If you are in between sizes, size down for a closer fit or up for a roomier kit.',
        fr: 'Coupe sport décontractée au niveau du corps. Si vous êtes entre deux tailles, choisissez la taille inférieure pour un ajustement plus près du corps ou supérieure pour un look plus ample.'
      }
    }
  ];

  // Asset URL resolver — reads from the themeAssetMap JSON written by layout.liquid.
  var _themeAssetMap = null;
  function getAssetMap() {
    if (_themeAssetMap !== null) return _themeAssetMap;
    var el = document.getElementById('themeAssetMap');
    if (!el) { _themeAssetMap = {}; return _themeAssetMap; }
    try { _themeAssetMap = JSON.parse(el.textContent); }
    catch (err) { _themeAssetMap = {}; }
    return _themeAssetMap;
  }
  function resolveAssetUrl(filename) {
    var map = getAssetMap();
    // Map normalized filenames to keys in themeAssetMap
    var lookup = {
      'crewneck_spec_illustration.png': 'crewneckSpec',
      'totebag_spec_illustration.png': 'toteSpec',
      'jersey_spec_illustration.png': 'jerseySpec',
      'stickers.webp': 'stickers',
      'wallet.webp': 'wallet'
    };
    var key = lookup[filename.replace(/^\//, '').toLowerCase()];
    if (key && map[key]) return map[key];
    return filename;
  }

  var _sgUnit = 'in';
  var _sgCurrentGuide = null;

  function setSizeGuideUnit(u) {
    _sgUnit = u;
    document.querySelectorAll('.sg-page-unit-btn').forEach(function (b) {
      var on = b.dataset.unit === u;
      b.classList.toggle('active', on);
      b.setAttribute('aria-selected', on ? 'true' : 'false');
    });
    var modalIn = document.getElementById('sgUnitIn');
    var modalCm = document.getElementById('sgUnitCm');
    if (modalIn && modalCm) {
      modalIn.classList.toggle('active', u === 'in');
      modalCm.classList.toggle('active', u === 'cm');
    }
    // Re-render whichever surface is open
    if (document.getElementById('sgPageBody')) renderStandaloneGuides();
    if (_sgCurrentGuide) renderModalGuide(_sgCurrentGuide);
  }
  window.setSizeGuideUnit = setSizeGuideUnit;

  function fmtMeasure(inches) {
    return _sgUnit === 'in' ? inches.toFixed(2) : (inches * 2.54).toFixed(1);
  }

  function buildGuideTable(g) {
    var sizes = pick(g.sizes) || [];
    var showHead = sizes.length > 1;
    var headHtml = '';
    if (showHead) {
      var topLeft = pick(g.groupLabel) || '';
      var headCells = [topLeft].concat(sizes).map(function (s) { return '<th>' + s + '</th>'; }).join('');
      headHtml = '<thead><tr>' + headCells + '</tr></thead>';
    }
    var useLetters = g.rowLetters === true;
    var bodyRows = g.measures.map(function (m, i) {
      var label = pick(m.label);
      var cells = m.in.map(function (v) { return '<td>' + fmtMeasure(v) + '</td>'; }).join('');
      var prefixHtml = useLetters
        ? '<span class="sg-row-letter">' + String.fromCharCode(65 + i) + '</span>' + label
        : (i + 1) + '. ' + label;
      return '<tr><td>' + prefixHtml + '</td>' + cells + '</tr>';
    }).join('');
    return headHtml + '<tbody>' + bodyRows + '</tbody>';
  }

  /* Standalone page renderer */
  function renderStandaloneGuides() {
    var body = document.getElementById('sgPageBody');
    if (!body) return;
    var lang = getLang();
    var guides = SIZE_GUIDES.filter(function (g) {
      var locales = g.locales || ['en'];
      return locales.indexOf(lang) !== -1;
    });
    body.innerHTML = guides.map(function (g) {
      var ill = g.illustration
        ? '<div class="sg-page-illustration"><img src="' + resolveAssetUrl(g.illustration) + '" alt="' + (pick(g.illustrationAlt) || '') + '"></div>'
        : '';
      return '' +
        '<section class="sg-page-section">' +
          '<h2 class="sg-page-section-title">' + pick(g.name) + '</h2>' +
          '<p class="sg-page-section-meta">' + pick(g.meta) + '</p>' +
          '<div class="sg-page-grid">' +
            '<div>' +
              '<div class="sg-page-table-wrap">' +
                '<table class="sg-page-table">' + buildGuideTable(g) + '</table>' +
              '</div>' +
              '<p class="sg-page-fit-note">' + pick(g.fitNote) + '</p>' +
            '</div>' +
            ill +
          '</div>' +
        '</section>';
    }).join('');
  }

  /* Standalone unit toggle wiring */
  function initSizeGuidePage() {
    var body = document.getElementById('sgPageBody');
    if (!body) return;
    document.querySelectorAll('.sg-page-unit-btn').forEach(function (b) {
      b.addEventListener('click', function () { setSizeGuideUnit(b.dataset.unit); });
    });
    renderStandaloneGuides();
  }

  /* Modal renderer */
  function renderModalGuide(g) {
    var table = document.getElementById('sgTable');
    var note = document.getElementById('sgNote');
    var diagram = document.getElementById('sgDiagram');
    if (table) table.innerHTML = buildGuideTable(g);
    if (note) note.textContent = pick(g.fitNote) || '';
    if (diagram) {
      if (g.illustration) {
        diagram.src = resolveAssetUrl(g.illustration);
        diagram.alt = pick(g.illustrationAlt) || '';
        diagram.style.display = '';
      } else {
        diagram.style.display = 'none';
      }
    }
  }

  function findGuideForHandle(handle) {
    if (!handle) return null;
    var lower = handle.toLowerCase();
    var lang = getLang();
    return SIZE_GUIDES.find(function (g) {
      var locales = g.locales || ['en'];
      if (locales.indexOf(lang) === -1) return false;
      if (g.handle === lower) return true;
      return g.handleAliases.some(function (a) { return lower.indexOf(a) !== -1; });
    }) || null;
  }

  function openSizeGuide() {
    var ov = document.getElementById('sgOverlay');
    if (!ov) return;
    ov.classList.add('open');
    ov.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
  }
  function closeSizeGuide() {
    var ov = document.getElementById('sgOverlay');
    if (!ov) return;
    ov.classList.remove('open');
    ov.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
  }
  function openSizeGuideForProduct(handle) {
    var g = findGuideForHandle(handle);
    if (!g) {
      // Fall back to first guide if we can't match
      g = SIZE_GUIDES[0];
    }
    _sgCurrentGuide = g;
    renderModalGuide(g);
    openSizeGuide();
  }
  window.openSizeGuide = openSizeGuide;
  window.closeSizeGuide = closeSizeGuide;
  window.openSizeGuideForProduct = openSizeGuideForProduct;

  /* ============================================================
     PRODUCT CARD QUICK-ADD (hover overlay on grid cards)
     ============================================================ */
  function initCardQuickAdd() {
    // Size button select (within a card's quick-add panel)
    document.addEventListener('click', function (e) {
      var sizeBtn = e.target.closest('.card-quick-size');
      if (!sizeBtn || sizeBtn.disabled || sizeBtn.classList.contains('unavail')) return;
      e.preventDefault();
      e.stopPropagation();
      var siblings = sizeBtn.closest('.card-quick-sizes').querySelectorAll('.card-quick-size');
      siblings.forEach(function (b) { b.classList.remove('active'); });
      sizeBtn.classList.add('active');
    });

    // Qty +/- (within a card's quick-add panel)
    document.addEventListener('click', function (e) {
      var qtyBtn = e.target.closest('.card-quick-qty-btn');
      if (!qtyBtn) return;
      e.preventDefault();
      e.stopPropagation();
      var num = qtyBtn.closest('.card-quick-qty').querySelector('.card-quick-qty-num');
      if (!num) return;
      var current = parseInt(num.textContent, 10) || 1;
      var delta = parseInt(qtyBtn.dataset.qtyDelta, 10) || 0;
      num.textContent = Math.max(1, current + delta);
    });

    // Add-to-cart from a card's quick-add panel
    document.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-quick-add-btn]');
      if (!btn) return;
      e.preventDefault();
      e.stopPropagation();

      var quickAdd = btn.closest('.card-quick-add');
      if (!quickAdd) return;
      var sizeIndex = parseInt(quickAdd.dataset.sizeIndex, 10);
      var variants;
      try { variants = JSON.parse(btn.dataset.variants); }
      catch (err) { console.error('quick-add: bad variants JSON', err); return; }
      if (!variants || !variants.length) return;

      var qtyEl = quickAdd.querySelector('.card-quick-qty-num');
      var qty = qtyEl ? Math.max(1, parseInt(qtyEl.textContent, 10) || 1) : 1;

      // Pick the variant: if there's a size option, find the variant matching the active size.
      // Otherwise just use the first available variant.
      var variantId;
      if (sizeIndex !== -1) {
        var activeSize = quickAdd.querySelector('.card-quick-size.active');
        if (activeSize) {
          var selectedSize = activeSize.dataset.size;
          var match = variants.find(function (v) {
            var opts = [v.option1, v.option2, v.option3];
            return opts[sizeIndex] === selectedSize && v.available;
          });
          if (!match) {
            match = variants.find(function (v) {
              var opts = [v.option1, v.option2, v.option3];
              return opts[sizeIndex] === selectedSize;
            });
          }
          if (match) variantId = match.id;
        }
      }
      if (!variantId) {
        var firstAvail = variants.find(function (v) { return v.available; }) || variants[0];
        if (firstAvail) variantId = firstAvail.id;
      }
      if (!variantId) {
        btn.textContent = pick(STRINGS.unavailable);
        return;
      }

      btn.disabled = true;
      btn.dataset.prevText = btn.dataset.prevText || btn.textContent;
      btn.textContent = pick(STRINGS.adding);

      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ id: variantId, quantity: qty })
      })
        .then(function (r) {
          if (!r.ok) return r.json().then(function (err) { throw err; });
          return r.json();
        })
        .then(function () { return refreshCart(); })
        .then(function () {
          btn.classList.add('confirmed');
          btn.textContent = qty > 1 ? pick(STRINGS.addedCheck) + ' (' + qty + ')' : pick(STRINGS.addedCheck);
          bumpCartBadge();
          setTimeout(function () {
            btn.textContent = btn.dataset.prevText || pick(STRINGS.addToCart);
            btn.classList.remove('confirmed');
            btn.disabled = false;
          }, 2000);
        })
        .catch(function (err) {
          console.error('quick-add failed', err);
          btn.textContent = (err && err.description) || pick(STRINGS.error);
          setTimeout(function () {
            btn.textContent = btn.dataset.prevText || pick(STRINGS.addToCart);
            btn.disabled = false;
          }, 2200);
        });
    });
  }

  /* ============================================================
     CART AJAX HELPERS
     ============================================================ */
  function refreshCart() {
    return fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (data) {
        var visibleCount = 0;
        (data.items || []).forEach(function (i) {
          if (i.handle !== 'sticker-pack') visibleCount += i.quantity;
        });
        var badge = document.getElementById('navCartCount');
        var btn = document.querySelector('.nav-cart-btn');
        if (badge) {
          badge.textContent = visibleCount;
          badge.setAttribute('data-empty', visibleCount === 0 ? 'true' : 'false');
        }
        if (btn) {
          var noun = pick(STRINGS.cartItem);
          var label = pick(STRINGS.cartLabel) + ' (' + visibleCount + ' ' + noun + (visibleCount === 1 ? '' : 's') + ')';
          btn.setAttribute('aria-label', label);
        }
        return data;
      })
      .catch(function (err) { console.error('cart refresh failed', err); });
  }

  function bumpCartBadge() {
    var badge = document.getElementById('navCartCount');
    if (!badge) return;
    badge.classList.remove('bump');
    void badge.offsetWidth; // restart animation
    badge.classList.add('bump');
  }

  /* PDP image counter — populates "X / N" and scroll sync */
  function initPdpCounter() {
    var stack = document.getElementById('pdpImages');
    var counter = document.getElementById('pdpCounter');
    if (!stack || !counter) return;
    var frames = stack.querySelectorAll('.pdp-image-frame');
    if (frames.length <= 1) {
      counter.innerHTML = '';
      counter.style.display = 'none';
      return;
    }
    counter.innerHTML =
      '<span class="pdp-counter-position">1 / ' + frames.length + '</span>' +
      '<button type="button" class="pdp-counter-arrow" onclick="pdpScrollToNext()" aria-label="' + pick(STRINGS.nextImage) + '">' +
      '<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.25" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
      '<path d="M6 9l6 6 6-6"/></svg></button>';
    counter.dataset.total = frames.length;

    var ticking = false;
    stack.addEventListener('scroll', function () {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(function () {
        var allFrames = stack.querySelectorAll('.pdp-image-frame');
        if (!allFrames.length) { ticking = false; return; }
        var isMobile = window.innerWidth <= 1024;
        var closest = 0, minDist = Infinity;
        if (isMobile) {
          var stackCenterX = stack.scrollLeft + stack.clientWidth / 2;
          allFrames.forEach(function (f, i) {
            var fc = f.offsetLeft + f.offsetWidth / 2;
            var d = Math.abs(fc - stackCenterX);
            if (d < minDist) { minDist = d; closest = i; }
          });
        } else {
          var stackCenterY = stack.scrollTop + stack.clientHeight / 2;
          allFrames.forEach(function (f, i) {
            var fc = f.offsetTop + f.offsetHeight / 2;
            var d = Math.abs(fc - stackCenterY);
            if (d < minDist) { minDist = d; closest = i; }
          });
        }
        var counterEl = document.getElementById('pdpCounter');
        if (counterEl && counterEl.dataset.total) {
          var total = parseInt(counterEl.dataset.total, 10);
          var positionEl = counterEl.querySelector('.pdp-counter-position');
          if (positionEl) positionEl.textContent = (closest + 1) + ' / ' + total;
          var atEnd = closest === total - 1;
          counterEl.classList.toggle('at-end', atEnd);
          var arrowBtn = counterEl.querySelector('.pdp-counter-arrow');
          if (arrowBtn) arrowBtn.setAttribute('aria-label', atEnd ? pick(STRINGS.backToFirst) : pick(STRINGS.nextImage));
        }
        ticking = false;
      });
    }, { passive: true });
  }

  function pdpScrollToNext() {
    var counter = document.getElementById('pdpCounter');
    if (!counter || !counter.dataset.total) return;
    var positionEl = counter.querySelector('.pdp-counter-position');
    var current = parseInt(((positionEl && positionEl.textContent) || '1 / 1').split(' / ')[0], 10) || 1;
    var total = parseInt(counter.dataset.total, 10);
    var targetIdx = current >= total ? 0 : current;
    pdpScrollToImage(targetIdx);
  }

  function pdpScrollToImage(idx) {
    var stack = document.getElementById('pdpImages');
    if (!stack) return;
    var frames = stack.querySelectorAll('.pdp-image-frame');
    if (!frames[idx]) return;
    var isMobile = window.innerWidth <= 1024;
    if (isMobile) {
      frames[idx].scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
    } else {
      stack.scrollTo({ top: frames[idx].offsetTop, behavior: 'smooth' });
    }
  }

  window.pdpScrollToNext = pdpScrollToNext;
  window.pdpScrollToImage = pdpScrollToImage;
  
  /* ============================================================
     INIT
     ============================================================ */
  function init() {
    // Drawer
    var hamb = getHamburger();
    if (hamb) hamb.addEventListener('click', toggleDrawer);
    var overlay = getOverlay();
    if (overlay) overlay.addEventListener('click', closeDrawer);

    // Mobile mega-menu open/back wiring
    document.querySelectorAll('[data-mega-open]').forEach(function (el) {
      el.addEventListener('click', function () { setMegaView(el.dataset.megaOpen); });
    });
    document.querySelectorAll('[data-mega-back]').forEach(function (el) {
      el.addEventListener('click', function () { setMegaView('main'); });
    });

    // Close drawer when navigating to a link inside it
    document.querySelectorAll('.mega-col-item').forEach(function (link) {
      link.addEventListener('click', function () { closeDrawer(); });
    });

    // ESC closes overlays
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') {
        closeLightbox();
        closeSizeGuide();
        if (getDrawer() && getDrawer().classList.contains('open')) closeDrawer();
      }
    });

    // Per-page initializers (each is a no-op on pages where their elements aren't present)
    initIntroReveal();
    initHeroCrossfade();
    initIntroParallax();
    initScrollTo();
    initFaq();
    initPdpVariantSelection();
    initPdpQty();
    initPdpAddToCart();
    initPdpScrollHandoff();
    initPdpCounter();
    initMobileStickyCart();
    initSizeGuidePage();
    initCardQuickAdd();

    // Cart badge sync on every page
    refreshCart();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }
  function initAll() {
    init();
    initCartLines();
    initPdpDots();
    initPdpScrollHandoff();
  }
  function initPdpScrollHandoff() {
    var EDGE_TOLERANCE = 1;
    function attach(pane) {
      if (!pane) return;
      pane.addEventListener('wheel', function(e) {
        var atBottom = pane.scrollTop + pane.clientHeight >= pane.scrollHeight - EDGE_TOLERANCE;
        var atTop = pane.scrollTop <= EDGE_TOLERANCE;
        if ((e.deltaY > 0 && atBottom) || (e.deltaY < 0 && atTop)) {
          e.preventDefault();
          window.scrollBy({ top: e.deltaY, left: 0, behavior: 'auto' });
        }
      }, { passive: false });
    }
    attach(document.getElementById('pdpImages'));
    attach(document.querySelector('.pdp-info'));
  }
  function initPdpDots() {
    var stack = document.getElementById('pdpImages');
    if (!stack) return;
    document.querySelectorAll('.pdp-dot').forEach(function(dot) {
      dot.addEventListener('click', function() {
        var idx = parseInt(dot.dataset.imgIndex || '0', 10);
        var frames = stack.querySelectorAll('.pdp-image-frame');
        if (frames[idx]) frames[idx].scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
      });
    });
    var ticking = false;
    stack.addEventListener('scroll', function() {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(function() {
        var frames = stack.querySelectorAll('.pdp-image-frame');
        var dotEls = document.querySelectorAll('.pdp-dot');
        if (frames.length && dotEls.length) {
          var stackCenter = stack.scrollLeft + stack.clientWidth / 2;
          var closest = 0, minDist = Infinity;
          frames.forEach(function(f, i) {
            var fc = f.offsetLeft + f.offsetWidth / 2;
            var d = Math.abs(fc - stackCenter);
            if (d < minDist) { minDist = d; closest = i; }
          });
          dotEls.forEach(function(d, i) { d.classList.toggle('is-active', i === closest); });
        }
        ticking = false;
      });
    }, { passive: true });
  }
  function initCartLines() {
    var form = document.querySelector('.cart-form');
    if (!form) return;
    form.querySelectorAll('.cart-qty-btn').forEach(function(btn) {
      btn.addEventListener('click', function() {
        var wrap = btn.closest('.cart-qty');
        if (!wrap) return;
        var input = wrap.querySelector('.cart-qty-input');
        if (!input) return;
        var delta = parseInt(btn.dataset.qtyDelta || '0', 10);
        input.value = Math.max(0, (parseInt(input.value, 10) || 0) + delta);
        form.submit();
      });
    });
    form.querySelectorAll('.cart-qty-input').forEach(function(input) {
      input.addEventListener('change', function() {
        input.value = Math.max(0, parseInt(input.value, 10) || 0);
        form.submit();
      });
    });
  }

// ===== STICKER PACK AUTO-ADD =====
(function initStickerPack() {
  var STICKER_HANDLE = 'sticker-pack';
  var stickerId = null;
  var lock = false;

  function getStickerId() {
    if (stickerId) return Promise.resolve(stickerId);
    return fetch('/products/' + STICKER_HANDLE + '.js')
      .then(function(r) { return r.ok ? r.json() : null; })
      .then(function(p) { stickerId = (p && p.variants && p.variants[0] && p.variants[0].id) || null; return stickerId; })
      .catch(function() { return null; });
  }

  function ensureStickerInCart() {
    if (lock) return Promise.resolve();
    lock = true;
    return getStickerId().then(function(sid) {
      if (!sid) { lock = false; return; }
      return fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
        .then(function(r) { return r.json(); })
        .then(function(cart) {
          var items = (cart && cart.items) || [];
          var hasSticker = items.some(function(i) { return i.variant_id == sid; });
          var hasOther = items.some(function(i) { return i.variant_id != sid; });
          if (hasOther && !hasSticker) {
            return fetch('/cart/add.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
              body: JSON.stringify({ id: sid, quantity: 1 })
            }).then(function() { if (location.pathname === '/cart') location.reload(); });
          }
          if (hasSticker && !hasOther) {
            // No paid items remain — remove the lingering free sticker so the cart is
            // genuinely empty and nothing can be carried into /checkout on its own.
            var stickerLine = items.filter(function(i) { return i.variant_id == sid; })[0];
            if (stickerLine) {
              return fetch('/cart/change.js', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
                body: JSON.stringify({ id: stickerLine.key, quantity: 0 })
              })
                .then(function(r) { return r.json(); })
                .then(function(newCart) {
                  var stillHasSticker = (newCart.items || []).some(function(i) { return i.variant_id == sid; });
                  if (!stillHasSticker && location.pathname === '/cart') {
                    location.reload();
                  } else {
                    refreshCart();
                  }
                });
            }
          }
        })
        .catch(function() {})
        .then(function() { lock = false; });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', ensureStickerInCart);
  } else {
    ensureStickerInCart();
  }

  var origFetch = window.fetch;
  window.fetch = function() {
    var args = arguments;
    var url = (typeof args[0] === 'string') ? args[0] : (args[0] && args[0].url) || '';
    var p = origFetch.apply(this, args);
    if (typeof url === 'string' && /\/cart\/(add|change|update)\.js/.test(url)) {
      p.then(function() { if (!lock) setTimeout(ensureStickerInCart, 100); });
    }
    return p;
  };
})();

// ===== QC-BLOCKED PRODUCT PURGE (French/Quebec) =====
(function initQcPurge() {
  // Products tagged qc-block — hidden from FR catalog and ineligible for
  // Quebec shipping. If they end up in a FR cart (added in EN, then user
  // switched), strip them on FR page load.
  var QC_BLOCKED_PRODUCT_IDS = [
    8451221291053, // Crewneck sweater
    8454214320173, // Oversized tote
    8454216646701  // Gold coin
  ];
  var lock = false;

  function purgeBlocked() {
    if (getLang() !== 'fr' || lock) return Promise.resolve();
    lock = true;
    return fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
      .then(function(r) { return r.json(); })
      .then(function(cart) {
        var items = (cart && cart.items) || [];
        var toRemove = items.filter(function(i) {
          return QC_BLOCKED_PRODUCT_IDS.indexOf(i.product_id) !== -1;
        });
        if (toRemove.length === 0) {
          lock = false;
          return;
        }
        // Sequentially remove each blocked line by its line key.
        return toRemove.reduce(function(p, item) {
          return p.then(function() {
            return fetch('/cart/change.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
              body: JSON.stringify({ id: item.key, quantity: 0 })
            });
          });
        }, Promise.resolve()).then(function() {
          lock = false;
          // If on the cart page, reload so the user sees the updated state.
          // Otherwise just refresh the cart badge.
          if (location.pathname.indexOf('/cart') !== -1) {
            location.reload();
          } else {
            refreshCart();
          }
        });
      })
      .catch(function(err) {
        console.error('QC purge failed', err);
        lock = false;
      });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', purgeBlocked);
  } else {
    purgeBlocked();
  }
})();
})();
